import React, { useEffect, useRef } from 'react';
import type { TextInput } from 'react-native';
import { TextField } from '@sweetgreen/garnish';

import { useFeatureFlag } from '@order/LaunchDarkly';
import { useLocalizationContext } from '@order/Localization';

import type { LocationResultsProps } from '../../../LocationsScreen.types';

// ────────────────────────────────────────────────────────────────────────────────

export const LocationSearchInput = (props: LocationSearchInputProps) => {
  const { searchString, onSearchSubmit, loading = false } = props;

  const shouldDisableSearchControlsWhileLoading = useFeatureFlag(
    'XP-3189-short-disable-search-controls-while-fetching-locations',
  );
  const shouldDisableTextField =
    shouldDisableSearchControlsWhileLoading && loading;

  const textFieldRef = useRef<TextInput>(null);
  const { t } = useLocalizationContext();

  // ─────────────────────────────────────────────────────────────────

  // clear search input if it's being emptied from the parent component
  useEffect(() => {
    const clearInput = textFieldRef?.current?.clear;
    const shouldClearSearchField = searchString === '';

    if (shouldClearSearchField) clearInput?.();
  }, [searchString]);

  // ─────────────────────────────────────────────────────────────────

  return (
    <TextField
      ref={textFieldRef}
      containerTestID="locationsearch.location-search-bar"
      submitButtonTestID="locationsearch.submit-button"
      testID="location-search-input"
      accessibilityLabel={t('location.search-placeholder')}
      placeholder={t('location.search-placeholder')}
      clearButtonType="text"
      value={searchString}
      onSubmit={onSearchSubmit}
      disabled={shouldDisableTextField}
      clearBtnLabel={t('general.clear')}
      clearBtnAccessibilityHint={t('general.clear-a11y-hint')}
      submitBtnAccessibilityLabel={t('general.submit')}
      submitAccessibilityHint={t('general.submit-a11y-hint')}
    />
  );
};

// ─── TYPES ──────────────────────────────────────────────────────────────────────

type LocationSearchInputProps = Pick<
  LocationResultsProps,
  'onSearchSubmit' | 'searchString' | 'loading'
>;
